import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormNumericInput, FormSuggest } from 'components/form-fields';

// Match the Employee microservice's Infringement raised trigger key and infringement kinds.
export const infringementRaisedTriggerKey = "INFRINGEMENTRAISED";
export const anyTypeInfringementKindKey = "ANYTYPE";
export const sameTypeInfringementKindKey = "SAMETYPE";
export const specificCodeInfringementKindKey = "SPECIFICCODE";

const defaultObject = {};
const defaultArray = [];

export function getInfringementRaisedTriggerType(reviewOptions) {
    return (reviewOptions?.triggerTypes ?? []).find((triggerType) => triggerType.key === infringementRaisedTriggerKey) ?? null;
}

function getSpecificTypeOptionId(infringementTypeId) {
    return `${specificCodeInfringementKindKey}-${infringementTypeId}`;
}

export function InfringementRaisedCondition(
    {
        id,
        condition,
        onChange,
        reviewOptions = defaultObject,
        loading = false,
        disabled = false
    }
) {

    // One searchable list covers the kind and, for a specific type, which type: the general
    // kinds first, then an entry for each active infringement type.
    const kindItems = useMemo(() => {
        const generalKinds = (getInfringementRaisedTriggerType(reviewOptions)?.infringementKinds ?? defaultArray)
            .filter((kind) => kind.key !== specificCodeInfringementKindKey)
            .map((kind) => {
                return {
                    id: kind.key,
                    name: kind.name,
                    infringementKind: kind.key,
                    infringementTypeId: null
                };
            });

        const specificTypes = (reviewOptions?.infringementTypes ?? defaultArray).map((infringementType) => {
            return {
                id: getSpecificTypeOptionId(infringementType.id),
                name: `type ${infringementType.code}`,
                infringementKind: specificCodeInfringementKindKey,
                infringementTypeId: infringementType.id
            };
        });

        return [...generalKinds, ...specificTypes];
    }, [reviewOptions]);

    let selectedKindId = condition.infringementKind;

    if (condition.infringementKind === specificCodeInfringementKindKey) {
        selectedKindId = getSpecificTypeOptionId(condition.infringementTypeId);
    }

    const singular = condition.conditionCount === 1;

    function onCountChange(value) {
        onChange({
            ...condition,
            conditionCount: value === "" ? null : value
        });
    }

    function onKindSelect(item) {
        onChange({
            ...condition,
            infringementKind: item.infringementKind,
            infringementTypeId: item.infringementTypeId
        });
    }

    return (
        <div className="inline-items">
            <div>when</div>
            <FormNumericInput
                id={`${id}-count`}
                min={1}
                max={1000}
                loading={loading}
                disabled={disabled}
                disabledInput={disabled}
                selectedValue={condition.conditionCount}
                onValueChange={onCountChange}
            />
            <div>{singular ? "infringement of" : "infringements of"}</div>
            <FormSuggest
                id={`${id}-kind`}
                items={kindItems}
                loading={loading}
                disabled={disabled}
                selectedValue={selectedKindId}
                onItemSelect={onKindSelect}
                clearSelection={false}
                itemsToReturn={1000}
                scrollableMenu
            />
            <div>{singular ? "is raised since the last review" : "are raised since the last review"}</div>
        </div>
    );
}

InfringementRaisedCondition.propTypes = {
    id: PropTypes.string.isRequired,
    condition: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    reviewOptions: PropTypes.object,
    loading: PropTypes.bool,
    disabled: PropTypes.bool
};
